/**
 * **素材を使う召喚を疑似イベント `material_summon` にする**（Issue #647）。**`duelEditor/` の外**。
 *
 * シンクロ・融合などの召喚は、core のイベントでは**素材の `move` が 1 枚ずつ**来て、モンスターが出る `spsummoning` は
 * **別の応答**で来る（実データで 3〜18 秒あと。素材を墓地に置く → EX デッキから探す → 場に置く）。1 枚目の素材の行からは
 * 「あと何枚来るか」も「何が出るか」も分からない（分岐レイヤーは未来を見ない）ので、ステータスの変化（`statChanges.js`）と同じく
 * **port がイベントの列を見渡して 1 行にまとめて差し込む**（ユーザー判断 2026-10-01）:
 *
 * - **どこに**: その召喚の**最後の素材の `move` の直後**。素材はこの行より前の `move` で墓地へ行っている（盤には居ない）。
 *   演出は盤を触らない変身バンク（ユーザー 2026-10-01）。時刻は位置合わせ（`/duel-timing`）で後から動かせる
 * - **まとめる素材**: 続いて来る素材の `move`（理由に SYNCHRO / FUSION / RITUAL / XYZ / LINK、またはアドバンス召喚のリリース＝RELEASE と SUMMON）で、種類が同じもの。
 *   間に挟まる問い合わせ・ヒントは飛ばし、ほかの core のイベントが来たら切る
 * - **出るモンスター**: その後の最初の `spsummoning`（アドバンス召喚は `summoning` かモンスターゾーンへの `set`。`new_phase` / `new_turn` まで）。
 *   まだ入力されていなければ null
 * - **キー**: 最後の素材の `move` と同じ応答（`op_seq`）で、`msg_offset` に `MATERIAL_SUMMON_OFFSET`（300000）を足した値
 *   （`start` の 200000・stat_change の 100000 台とぶつからず、サーバーの検証の 6 桁に入る）
 * - **index**: 最後の素材の `move` の index + 0.25（stat_change の +0.5 とぶつけない。盤面のステップは持たない）
 *
 * payload（`data`）: `{ kind, player, materials: [{ uid, code, level, isTuner, type, from }], totalLevel,
 *   result: { uid, code, level, type, loc, faceDown } | null, source: { code, uid, chain } | null }`（source ＝ 処理中の効果のカード。
 *   儀式魔法・融合魔法など。ルールの召喚は null）。kind は synchro / fusion / ritual / xyz / link / tribute。`level` / `isTuner` は `card_info`（OcgGetState。cards.cdb）から。
 * 無ければ null（レベルの変化は見ない＝カードの元々のレベル）
 */

export const MATERIAL_SUMMON_TYPE = 'material_summon';
/** 疑似イベントの `msg_offset` に足す値 */
export const MATERIAL_SUMMON_OFFSET = 300000;

/** core の `REASON_*`（`duelEffects.js` と同じ値） */
const REASON = Object.freeze({
  RELEASE: 0x2, SUMMON: 0x10, MATERIAL: 0x8, FUSION: 0x40000, SYNCHRO: 0x80000, RITUAL: 0x100000, XYZ: 0x200000, LINK: 0x10000000,
});
const TYPE_TUNER = 0x1000;
const UNTIL = new Set(['new_phase', 'new_turn']);

/** 素材の種類（素材の move でなければ null） */
export function materialKindOfReason(reason = 0) {
  if (reason & REASON.SYNCHRO) return 'synchro';
  if (reason & REASON.FUSION) return 'fusion';
  if (reason & REASON.RITUAL) return 'ritual';
  if (reason & REASON.XYZ) return 'xyz';
  if (reason & REASON.LINK) return 'link';
  // アドバンス召喚のリリース（RELEASE | MATERIAL | SUMMON。コストのリリースは SUMMON を持たない）
  if ((reason & REASON.RELEASE) && (reason & REASON.SUMMON)) return 'tribute';
  return null;
}

/** 出るモンスターのイベント（アドバンス召喚は通常召喚の宣言・セット、ほかは特殊召喚の宣言） */
const LOCATION_MZONE = 4;
const isResult = (kind, ev) => (kind === 'tribute'
  ? ev.type === 'summoning' || (ev.type === 'set' && ev.data?.card?.loc?.location === LOCATION_MZONE)
  : ev.type === 'spsummoning');

const isCore = (ev) => ev?.kind === 'event';
const kindOf = (ev) => (isCore(ev) && ev.type === 'move' ? materialKindOfReason(ev.data?.reason ?? 0) : null);
const uidOf = (c) => (typeof c === 'string' ? c : c?.uid ?? null);
const num = (v) => (Number.isFinite(v) ? v : null);

function infoOf(cardInfo, code) {
  const i = cardInfo?.[String(code)] ?? null;
  return { level: num(i?.level), type: num(i?.type), isTuner: i && Number.isFinite(i.type) ? Boolean(i.type & TYPE_TUNER) : null };
}

/**
 * @param events イベント列（`index` の昇順）
 * @param cardInfo `OcgGetState` の `card_info`（`{ code: { type, level } }`。無くてもよい）
 * @returns 疑似イベントを差し込んだ列（素材を使う召喚が無ければ同じ参照）
 */
export function withMaterialSummonEvents({ events = [], cardInfo = null } = {}) {
  /** 元の位置（配列の添字）→ その直後に入れる疑似イベント */
  const after = new Map();
  let i = 0;
  while (i < events.length) {
    const kind = kindOf(events[i]);
    if (!kind) { i += 1; continue; }
    // 続く素材の move（同じ種類）を集める。問い合わせ・ヒントは飛ばし、ほかの core のイベントで切る
    const mats = [i];
    let j = i + 1;
    for (; j < events.length; j += 1) {
      const ev = events[j];
      if (!isCore(ev)) continue;
      if (kindOf(ev) === kind) { mats.push(j); continue; }
      break;
    }
    const last = events[mats[mats.length - 1]];
    // 出るモンスター: その後の最初の spsummoning（アドバンス召喚は summoning / モンスターゾーンへの set。フェイズ・ターンまで）
    let result = null;
    for (let k = mats[mats.length - 1] + 1; k < events.length; k += 1) {
      const ev = events[k];
      if (!isCore(ev)) continue;
      if (UNTIL.has(ev.type)) break;
      if (isResult(kind, ev)) {
        const code = ev.data?.code || null;
        const ci = infoOf(cardInfo, code);
        result = {
          uid: uidOf(ev.data?.card), code, level: ci.level, type: ci.type, loc: ev.data?.card?.loc ?? null,
          // セット（裏側）で出たか。演出は絵を見せない
          faceDown: ev.type === 'set',
        };
        break;
      }
    }
    const materials = mats.map((m) => {
      const d = events[m].data ?? {};
      const ci = infoOf(cardInfo, d.code);
      return { uid: uidOf(d.card), code: d.code ?? null, level: ci.level, isTuner: ci.isTuner, type: ci.type, from: d.from?.loc ?? null };
    });
    const levels = materials.map((m) => m.level);
    const player = last.data?.from?.loc?.controller ?? last.data?.to?.loc?.controller ?? last.turn_player ?? 0;
    // どの効果の処理中か（儀式魔法・融合魔法など。処理中のリンク、無ければ積まれている一番上）。ルールの召喚（シンクロなど）は null
    const stack = last.chain_stack ?? [];
    const link = stack.find((c) => c?.chain === last.solving) ?? (last.solving != null ? stack[stack.length - 1] : null) ?? null;
    const source = link ? { code: link.code ?? null, uid: link.card ?? null, chain: link.chain ?? null } : null;
    after.set(mats[mats.length - 1], {
      id: `${last.id ?? 'm'}:material_summon`,
      index: (last.index ?? 0) + 0.25,
      kind: 'event',
      type: MATERIAL_SUMMON_TYPE,
      turn: last.turn ?? 0,
      turn_player: last.turn_player ?? 0,
      phase: last.phase ?? null,
      chain_stack: last.chain_stack ?? [],
      solving: last.solving ?? null,
      op_seq: last.op_seq ?? null,
      msg_offset: MATERIAL_SUMMON_OFFSET + (last.msg_offset ?? 0),
      data: {
        kind,
        player: player === 1 ? 1 : 0,
        materials,
        totalLevel: levels.every((l) => l != null) ? levels.reduce((a, b) => a + b, 0) : null,
        result,
        source,
      },
    });
    i = j;
  }
  if (!after.size) return events;
  const out = [];
  events.forEach((ev, k) => {
    out.push(ev);
    if (after.has(k)) out.push(after.get(k));
  });
  return out;
}
